import { Button, ProgressBar } from "react-bootstrap";
import styles from "./AnalyseInspector.module.css";
import MultiSelectButtonGroup from "./MultiSelectButtonGroup";
import TeamIcon from "./TeamIcon";
import RangeSlider from "react-bootstrap-range-slider";
import { getPlaylistOpponentTeamKey } from "../utils/playlist";
import { TeamsData } from "../pages/Matches";
import { Playlist } from "../utils/types";
import { databaseToBuyType } from "../utils/economy";

// Define the Player interface to match the structure in Analyse.tsx
interface Player {
  name: string;
  team_key: string | undefined;
  steamId: number;
  nameFromOfficialMatch: boolean;
}

// Define Players interface
interface Players {
  [steamId: number]: Player;
}

interface AnalyseInspectorProps {
  progress: number;
  playlistLength: number;
  roundsData: any[] | undefined;
  playlist: Playlist;
  teams: TeamsData;
  currentAnalysisType: string[];
  setCurrentAnalysisType: (value: string[]) => void;
  selectOpponentTeam: boolean;
  setSelectOpponentTeam: (value: boolean) => void;
  currentSides: string[];
  setCurrentSides: (value: string[]) => void;
  currentEconomyType: string[];
  setCurrentEconomyType: (value: string[]) => void;
  currentHeroBuyTypes: string[];
  setCurrentHeroBuyTypes: (value: string[]) => void;
  currentOpponentBuyTypes: string[];
  setCurrentOpponentBuyTypes: (value: string[]) => void;
  currentHeroAWPType: string[];
  setCurrentHeroAWPType: (value: string[]) => void;
  currentOpponentAWPType: string[];
  setCurrentOpponentAWPType: (value: string[]) => void;
  currentPlayers: string[];
  setCurrentPlayers: (value: string[]) => void;
  currentVisibility: string[];
  setCurrentVisibility: (value: string[]) => void;
  heatmapSmoothing: number;
  setHeatmapSmoothing: (value: number) => void;
  filteredRoundCount: number;
  players: Players;
  playerColors: Map<number, string>;
  resetActiveRound: () => void;
  resetSelectedProjectiles: () => void;
  setAutoPlay: (value: boolean) => void;
  onSelectMatchesClick: () => void;
  visibilityOptions: {
    [key: string]: {
      main: string;
      extra: string;
      transform: string;
    }
  };
  currentVersusTypes: string[];
  setCurrentVersusTypes: (value: string[]) => void;
  currentBombsites: string[];
  setCurrentBombsites: (value: string[]) => void;
}

const AnalyseInspector = ({
  progress,
  playlistLength,
  roundsData,
  playlist,
  teams,
  currentAnalysisType,
  setCurrentAnalysisType,
  selectOpponentTeam,
  setSelectOpponentTeam,
  currentSides,
  setCurrentSides,
  currentEconomyType,
  setCurrentEconomyType,
  currentHeroBuyTypes,
  setCurrentHeroBuyTypes,
  currentOpponentBuyTypes,
  setCurrentOpponentBuyTypes,
  currentHeroAWPType,
  setCurrentHeroAWPType,
  currentOpponentAWPType,
  setCurrentOpponentAWPType,
  currentPlayers,
  setCurrentPlayers,
  currentVisibility,
  setCurrentVisibility,
  heatmapSmoothing,
  setHeatmapSmoothing,
  filteredRoundCount,
  players,
  playerColors,
  resetActiveRound,
  resetSelectedProjectiles,
  setAutoPlay,
  onSelectMatchesClick,
  visibilityOptions,
  currentVersusTypes,
  setCurrentVersusTypes,
  currentBombsites,
  setCurrentBombsites
}: AnalyseInspectorProps) => {
  return (
    <>
      <div className={styles.progressBarWrapper}>
        <div style={{ width: '100%' }}>
          {progress < 1 &&
            <span style={{ fontSize: '12px', color: '#666' }}>Loading rounds ({roundsData ? roundsData.length : 0}/{playlistLength})...</span>
          }
          {progress >= 1 &&
            <span style={{ fontSize: '12px', color: '#666' }}>Done ({roundsData ? roundsData.length : 0}/{playlistLength}).</span>
          }
          <ProgressBar
            className={progress < 1 ? "analysisProgressBar" : "analysisProgressBarDone"}
            animated={true}
            striped={true}
            now={Math.round(progress * 100)}
            label={`${Math.round(progress * 100)}%`}
            style={{ marginBottom: '10px', width: '100%' }}
          />
        </div>
      </div>
      <div className={styles.menuRow}>
        <MultiSelectButtonGroup
          groupId="analysis-type"
          options={[
            { key: 'replay', label: 'Default' },
            { key: 'heatmap', label: 'Heatmap' },
            { key: 'grenades', label: 'Grenades' }
          ]}
          value={currentAnalysisType}
          multiSelect={false}
          allowEmpty={false}
          flexDirection="row"
          onSelect={(value) => {
            resetActiveRound();
            setAutoPlay(false);
            setCurrentAnalysisType(Array.isArray(value) ? value : [value])
          }}
        />
      </div>
      <div className={styles.menuRow}>
        <MultiSelectButtonGroup
          groupId="opponent-selection"
          options={[
            { key: 'hero', label: <><div style={{ width: '100%'}}><TeamIcon size={20} name={playlist.team_key || ''} flag={undefined} />{teams[playlist.team_key || '']?.name || playlist.team_key}</div></> },
            { key: 'opponent', label: <><div style={{ width: '100%'}}>{getPlaylistOpponentTeamKey(playlist) !== undefined && <TeamIcon size={20} name={getPlaylistOpponentTeamKey(playlist) || ''} flag={undefined} />}{getPlaylistOpponentTeamKey(playlist) !== undefined && teams[getPlaylistOpponentTeamKey(playlist) || '']?.name || 'Opponent'}</div></> },
          ]}
          value={selectOpponentTeam ? ['opponent'] : ['hero']}
          multiSelect={false}
          allowEmpty={false}
          flexDirection="row"
          onSelect={(value) => {
            resetActiveRound();
            setAutoPlay(false);
            setSelectOpponentTeam(Array.isArray(value) ? value.includes('opponent') : value === 'opponent')
            if (currentAnalysisType[0] === 'grenades') {
              resetSelectedProjectiles();
            }
            // If we switched from hero to opponent or vice versa, invert the side
            if ((Array.isArray(value) ? value.includes('opponent') : value === 'opponent') !== selectOpponentTeam) {
              setCurrentSides(currentSides.map(side => side === "T" ? "CT" : "T"));
            }
          }}
        />
      </div>
      {playlist.side !== undefined &&
        <div className={styles.menuRow}>
          <MultiSelectButtonGroup
            groupId="side-type"
            options={["T", "CT"]
              .map((side: string, index: number) => ({
                key: side,
                label: side
              }))
            }
            value={currentSides}
            multiSelect={false}
            allowEmpty={false}
            flexDirection="row"
            onSelect={(value) => {
              resetActiveRound();
              resetSelectedProjectiles();
              setCurrentSides(Array.isArray(value) ? value : [value])
            }}
          />
        </div>
      }
      <div className={styles.menuRow}>
        <MultiSelectButtonGroup
          groupId="economy-type"
          options={[
            { key: 'full', label: 'Full Buy' },
            { key: 'pistol', label: 'Pistols' },
            { key: 'custom', label: 'Custom' }
          ]}
          value={currentEconomyType}
          multiSelect={false}
          allowEmpty={true}
          flexDirection="row"
          onSelect={(value) => {
            resetActiveRound();
            resetSelectedProjectiles();
            setCurrentEconomyType(Array.isArray(value) ? value : [value])
          }}
        />
      </div>
      {currentEconomyType.length > 0 && currentEconomyType[0] === "custom" && <>
        <div className={styles.menuRow} style={{ marginBottom: '7px' }}>
          <TeamIcon size={20} name={playlist.team_key || ''} flag={undefined} />
          <div className={styles.menuSubRow} style={{ marginBottom: '-1px' }}>
            <MultiSelectButtonGroup
              groupId="custom-hero-buy-type"
              options={[
                { key: 'Full', label: 'Full' },
                { key: 'Semi', label: 'Semi' },
                { key: 'Eco+', label: 'Eco+' },
                { key: 'Eco', label: 'Eco' }
              ]}
              value={currentHeroBuyTypes}
              multiSelect={true}
              allowEmpty={true}
              flexDirection="row"
              onSelect={(value) => {
                resetActiveRound();
                resetSelectedProjectiles();
                setCurrentHeroBuyTypes(Array.isArray(value) ? value : [value])
              }}
            />
            <MultiSelectButtonGroup
              groupId="custom-hero-awp-type"
              options={[
                { key: 'awp', label: 'AWP' },
                { key: 'no-awp', label: 'No AWP' },
              ]}
              value={currentHeroAWPType}
              multiSelect={false}
              allowEmpty={true}
              flexDirection="row"
              onSelect={(value) => {
                resetActiveRound();
                resetSelectedProjectiles();
                setCurrentHeroAWPType(Array.isArray(value) ? value : [value])
              }}
            />
          </div>
        </div>
        <div className={styles.menuRow}>
          <TeamIcon size={20} name={getPlaylistOpponentTeamKey(playlist) || ''} flag={undefined} />
          <div className={styles.menuSubRow} style={{ marginBottom: '-1px' }}>
          <MultiSelectButtonGroup
            groupId="custom-opponent-buy-type"
            options={[
              { key: 'Full', label: 'Full' },
              { key: 'Semi', label: 'Semi' },
              { key: 'Eco+', label: 'Eco+' },
              { key: 'Eco', label: 'Eco' }
            ]}
            value={currentOpponentBuyTypes}
            multiSelect={true}
            allowEmpty={true}
            flexDirection="row"
            onSelect={(value) => {
              resetActiveRound();
              resetSelectedProjectiles();
              setCurrentOpponentBuyTypes(Array.isArray(value) ? value : [value])
            }}
          />
          <MultiSelectButtonGroup
              groupId="custom-opponent-awp-type"
              options={[
                { key: 'awp', label: 'AWP' },
                { key: 'no-awp', label: 'No AWP' },
              ]}
              value={currentOpponentAWPType}
              multiSelect={false}
              allowEmpty={true}
              flexDirection="row"
              onSelect={(value) => {
                resetActiveRound();
                resetSelectedProjectiles();
                setCurrentOpponentAWPType(Array.isArray(value) ? value : [value])
              }}
            />
          </div>
        </div>
      </>
      }
      <div className={styles.menuRow}>
        <MultiSelectButtonGroup
          groupId="versus-type"
          options={[
            { key: '5v4', label: '5v4' },
            { key: '4v5', label: '4v5' },
            { key: '4v4', label: '4v4' },
          ]}
          value={currentVersusTypes}
          multiSelect={false}
          allowEmpty={true}
          flexDirection="row"
          onSelect={(value) => {
            resetActiveRound();
            resetSelectedProjectiles();
            setCurrentVersusTypes(Array.isArray(value) ? value : [value])
          }}
        />
      </div>
      <div className={styles.menuRow}>
        <MultiSelectButtonGroup
          groupId="bombsite-type"
          options={[
            { key: 'A', label: 'A-site' },
            { key: 'B', label: 'B-site' },
          ]}
          value={currentBombsites}
          multiSelect={false}
          allowEmpty={true}
          flexDirection="row"
          onSelect={(value) => {
            resetActiveRound();
            resetSelectedProjectiles();
            setCurrentBombsites(Array.isArray(value) ? value : [value])
          }}
        />
      </div>
      <div className={styles.menuRow}>
        {Object.values(players).length > 0 && Object.values(players).every(player => player.team_key === Object.values(players)[0]?.team_key) ? (
          <MultiSelectButtonGroup
            groupId="current-player"
            options={Object.values(players)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((player) => {
                return {
                  key: player.steamId.toString(),
                  label: (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <TeamIcon size={20} name={player.team_key || ''} flag={undefined} />
                        <span style={{ marginLeft: '5px' }}>{player.name}</span>
                      </div>
                      <div
                        style={{
                          width: '15px',
                          height: '15px',
                          backgroundColor: playerColors.get(player.steamId),
                          borderRadius: '50%',
                          marginLeft: 'auto',
                          opacity: currentAnalysisType[0] === 'replay' && (currentPlayers.length === 0 || currentPlayers.length > 0 && currentPlayers.includes(player.steamId.toString())) ? 1 : 0.25
                        }}
                      />
                    </div>
                  )
                };
              })}
            value={currentPlayers}
            multiSelect={false}
            multiSelectWithControlKey={true}
            allowEmpty={true}
            flexDirection="column"
            onSelect={(value) => {
              setCurrentPlayers(Array.isArray(value) ? value : [value]);
            }}
          />
        ) : (
          <div style={{ padding: '10px', color: '#666' }}>
            {Object.values(players).length === 0 ? "No rounds selected" : "Multiple teams selected"}
          </div>
        )}
      </div>
      {(currentAnalysisType[0] === 'replay' || currentAnalysisType[0] === 'grenades') &&
        <div className={styles.menuRow}>
          <MultiSelectButtonGroup
            groupId="projectile-type"
            options={Object.keys(visibilityOptions)
              .map((projectileType: string) => ({
                key: projectileType,
                label: (
                  <svg className={styles.projectileIcon}>
                    <g key={`projectile_icon_${projectileType}`}>
                      <path
                        transform={visibilityOptions[projectileType]['transform']}
                        d={visibilityOptions[projectileType]['main']}
                        fill={currentVisibility.length === 0 || currentVisibility.includes(projectileType) ? `rgba(128,128,128,1)` : `rgba(64,64,64,1)`}
                      />
                      <path
                        transform={visibilityOptions[projectileType]['transform']}
                        d={visibilityOptions[projectileType]['extra']}
                        fill={currentVisibility.length === 0 || currentVisibility.includes(projectileType) ? `rgba(224,224,224,1)` : `rgba(96,96,96,1)`}
                      />
                    </g>
                  </svg>
                )
              }))
            }
            value={currentVisibility}
            multiSelect={false}
            multiSelectWithControlKey={true}
            allowEmpty={true}
            flexDirection="row"
            buttonStyle={{ paddingLeft: '2px', paddingRight: '2px' }}
            onSelect={(value) => {
              setCurrentVisibility(Array.isArray(value) ? value : [value])
            }}
          />
        </div>
      }
      {currentAnalysisType[0] === 'heatmap' && <>
        <div className={styles.menuRow} style={{ flexDirection: 'column' }}>
          <div style={{ display: "flex", alignItems: "center", width: '100%' }}>
            <label style={{ width: '120px', marginRight: "20px", fontSize: '14px' }}>Smoothing</label>
            <RangeSlider
              value={heatmapSmoothing}
              onMouseUp={(event: any) => {
                setHeatmapSmoothing(event.target.value)
              }}
              onMouseDown={(event: any) => {
                setHeatmapSmoothing(event.target.value)
              }}
              onChange={event => setHeatmapSmoothing(parseFloat(event.target.value))}
              min={0.33}
              max={1.5}
              step={0.01}
              tooltip="off"
              variant="info"
            />
          </div>
        </div>
      </>
      }
      <div className={styles.menuRow} style={{ alignItems: 'top' }}>
        <Button variant="primary" onClick={onSelectMatchesClick}><i className="bi bi-search" style={{ marginRight: '5px' }} /> Matches</Button>
        <span style={{ color: filteredRoundCount === 0 ? '#f44' : '#bbb', marginRight: '7px', marginLeft: 'auto' }}>{filteredRoundCount}</span><span style={{ color: '#555' }}>rounds</span>
      </div>
    </>
  );
};

export default AnalyseInspector; 